import React, { Fragment, useEffect, useRef } from 'react';
import { Helmet } from 'react-helmet';
import { Props } from '../types';
import { Headline } from '../props/headline';
import { AdobeTracking } from '../../tracking';

type PageProps = Props<{
  headline: Headline[];
  embed_code: string;
  description: string;
  show_in_new_tab: boolean;
  link_text: string;
}>;

export const RochePodigeeEpisode = ({ blok, slot }: PageProps): JSX.Element => {
  const divRef = useRef(null);
  const episodeUrl = useRef(null);

  const parseEpisodeUrl = (embedCode) => {
    // eslint-disable-next-line prefer-regex-literals
    const regex = new RegExp('data-configuration="([^\\s]*)"');
    const matches = embedCode.match(regex);
    return matches && matches[1] ? matches[1] : undefined;
  };

  useEffect(() => {
    const embedCode: string = blok.embed_code;
    if (!embedCode) { return; }
    const parsedUrl = parseEpisodeUrl(embedCode);
    if (parsedUrl) {
      episodeUrl.current = parsedUrl;
    }
    // eslint-disable-next-line prefer-regex-literals
    const regex = new RegExp('src="([^=]*)"');
    const matches = embedCode.match(regex);
    if (matches && matches[1]) {
      const src = matches[1];
      const child = document.createElement('script');
      child.setAttribute('src', src);
      divRef.current.appendChild(child);
    }

    const iframes = document.getElementsByTagName('iframe');
    Array.from(iframes).forEach((iframeElement) => {
      const iframe = iframeElement;
      if (!iframe.classList.contains('podigee-podcast-player')) return;

      iframe.onload = () => {
        try {
          const player = new playerjs.Player(iframe);
          let durationTotal: number;
          const milestones = {
            progress_10: false,
            progress_25: false,
            progress_50: false,
            progress_75: false,
          };

          player.on('timeupdate', ({ seconds, duration }) => {
            if (!durationTotal) durationTotal = Math.round(duration);
            if (!durationTotal) return;

            const progress = (seconds / durationTotal) * 100;

            [10, 25, 50, 75].forEach((milestone) => {
              const key = `progress_${milestone}` as keyof typeof milestones;

              if (progress >= milestone && !milestones[key]) {
                AdobeTracking(
                  {
                    name: iframe.id,
                    url: episodeUrl.current,
                    provider: 'podigee',
                    durationTotal,
                    progress: milestone,
                  },
                  'audioProgress',
                );
                milestones[key] = true;
              }
            });
          });

          player.on('ready', () => {
            player.on('play', () => {
              // waiting time to timeupdate event fire
              const waitingTimeInMS = 500;
              setTimeout(() => {
                AdobeTracking(
                  {
                    name: iframe.id,
                    url: episodeUrl.current,
                    provider: 'podigee',
                    durationTotal,
                  },
                  'audioStart',
                );
              }, waitingTimeInMS);
            });

            player.on('ended', () => {
              AdobeTracking(
                {
                  name: iframe.id,
                  url: episodeUrl.current,
                  provider: 'podigee',
                  durationTotal,
                },
                'audioComplete',
              );
            });
          });
        } catch (error) {
          // eslint-disable-next-line no-console
          console.error('Error initializing player:', error);
        }
      };
    });
  });

  return (
    <Fragment>
      <Helmet>
        <script type="text/javascript" src="//cdn.embed.ly/player-0.0.12.min.js" />
      </Helmet>
      <roche-grid layout="12" slot={slot}>
        {blok?.headline && blok.headline[0]
          && <roche-headline {...blok.headline[0]}></roche-headline>}
        <div ref={(ref) => { divRef.current = ref; }}></div>
        <div dangerouslySetInnerHTML={{ __html: blok.embed_code }}></div>
        {blok?.show_in_new_tab && <roche-generic-link text={blok?.link_text} icon="new-window"
          style={{ marginBottom: '1rem' }} url={episodeUrl.current} target="_blank"></roche-generic-link>}
        {blok?.description && <div style={{ whiteSpace: 'pre-wrap' }}>{blok.description}</div>}
      </roche-grid>
    </Fragment>
  );
};
